import { FunctionComponent } from 'react'
import { Icon, RoundedButton } from '../../../shared'
import { MailPatientDetectionProps } from './MailPatientDetection.model'
import { getV3ColorCssVariable } from '../../../../design-system/colors_v3'
import { useGetIdentityState } from '../../../../hooks/utils/patient/useGetIdentityState.hook'

const MailPatientDetection: FunctionComponent<MailPatientDetectionProps> = ({
  isFetching,
  patient,
  onUpdate,
  onCreate,
  hasPatientBeenCreated,
}) => {
  const { identityStatus } = patient
  const identityState = useGetIdentityState(identityStatus)

  const InfoWrapper = ({ children }: { children: React.ReactNode }) => (
    <div className="rounded-lg bg-shades-8 max-w-xs p-3">{children}</div>
  )

  // --- Loading State ---
  if (isFetching) {
    return (
      <InfoWrapper>
        <div role="status" className="max-w-sm animate-pulse">
          <div className="h-5 bg-gray-200 rounded-full dark:bg-gray-700 w-48 mb-4"></div>
          <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 mb-2.5"></div>
          <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 mb-2.5"></div>
          <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 w-4/5 mb-2.5"></div>
          <span className="sr-only">Loading...</span>
        </div>
      </InfoWrapper>
    )
  }
  // --- Patient Created from Mail State ---
  if (hasPatientBeenCreated && patient?.id) {
    return (
      <InfoWrapper>
        <span className="text-shades-2 font-semibold mb-2">Patient créé dans Follow !</span>
        <span className="text-shades-4 text-xs flex my-2 gap-1 font-semibold">
          <span className="flex gap-1">Statut de l'identité :</span>
          <span className="flex gap-1">
            <span className="mt-0.5">
              <Icon
                icon="bullet"
                color={getV3ColorCssVariable('status', identityState.color)}
                size="pico"
              />
            </span>
            {identityState?.label}
          </span>
        </span>
        <p className="text-shades-4 text-xs mb-3">
          Le patient a été créé avec succès. Vous pouvez désormais le retrouver dans votre base.
          {patient.needUpdate &&
            ' Voulez-vous vérifier et mettre à jour les informations du patient ?'}
        </p>
        {patient.needUpdate && (
          <RoundedButton
            disabled={identityStatus?.fictitious || identityStatus?.suspicious}
            icon="reload"
            label="Mettre à jour le patient"
            size="micro"
            onClick={onUpdate}
            testId="button-mss-patient_infos-update"
          />
        )}
      </InfoWrapper>
    )
  }
  if (hasPatientBeenCreated && patient?.id) {
    return (
      <InfoWrapper>
        <span className="text-shades-2 font-semibold mb-2">Patient créé dans Follow !</span>
        <span className="text-shades-4 text-xs flex my-2 gap-1 font-semibold">
          <span className={'flex gap-1'}>Statut de l'identité : </span>
          <span className={'flex gap-1'}>
            <span className={'mt-0.5'}>
              <Icon
                icon="bullet"
                color={getV3ColorCssVariable('status', identityState.color)}
                size="pico"
              />
            </span>
            {identityState?.label}
          </span>
        </span>

        <p className="text-shades-4 text-xs mb-3">
          Le patient a été créé avec succès. Vous pouvez désormais le retrouver dans votre base.
          {patient.needUpdate &&
            'Voulez-vous vérifier et mettre à jour les informations du patient ?'}
        </p>
        {patient.needUpdate && (
          <RoundedButton
            disabled={identityStatus?.fictitious || identityStatus?.suspicious}
            icon="reload"
            label="Mettre à jour le patient"
            size="micro"
            onClick={onUpdate}
            testId="button-mss-patient_infos-update"
          />
        )}
      </InfoWrapper>
    )
  }
  return (
    <div className="rounded-lg bg-shades-8 max-w-xs p-3">
      {patient.id ? (
        <>
          <span className="text-shades-2 font-semibold mb-2">Patient déjà sur Follow !</span>
          <span className="text-shades-4 text-xs flex my-2 gap-1 font-semibold">
            <span className={'flex gap-1'}> Statut de l'identité : </span>
            <span className={'flex gap-1'}>
              {' '}
              <span className={'mt-0.5'}>
                {' '}
                <Icon
                  icon="bullet"
                  color={getV3ColorCssVariable('status', identityState.color)}
                  size="pico"
                />
              </span>
              {identityState?.label}
            </span>
          </span>

          {identityStatus?.suspicious && (
            <span className="text-red-500 text-xs flex my-2 gap-1 font-semibold">
              <span className={'flex gap-1'}>Identitée marquée comme douteuse </span>
            </span>
          )}
          {identityStatus?.fictitious && (
            <span className="text-red-500 text-xs flex my-2 gap-1 font-semibold">
              <span className={'flex gap-1'}>Identitée marquée comme fictive </span>
            </span>
          )}
          {patient.needUpdate ? (
            <>
              <p className="text-shades-4 text-xs mb-3">
                Ce patient figure déjà dans votre base de patient sur Follow. Voulez-vous vérifier
                et mettre à jour les informations du patient ?
              </p>
              <RoundedButton
                disabled={identityStatus?.fictitious || identityStatus?.suspicious}
                icon="reload"
                label="Mettre à jour le patient"
                size="micro"
                onClick={onUpdate}
                testId="button-mss-patient_infos-update"
              />
            </>
          ) : (
            <p className="text-shades-4 text-xs">
              Ce patient figure dans votre base patient. Ses informations personnelles sont à jour.
            </p>
          )}
        </>
      ) : (
        <>
          <span className="text-shades-2 font-semibold mb-2">Nouveau patient trouvé !</span>
          <p className="text-shades-4 text-xs mb-3">
            Ce patient ne figure pas dans votre base de patient. Voulez-vous créer un patient avec
            ses informations ?
          </p>
          {!patient.inseeNumber && (
            <p className="text-shades-4 text-xs mb-3">
              Point de vigilance : le matricule INS du patient n’a pas été trouvé. Cette information
              sera donc manquante.
            </p>
          )}
          <RoundedButton
            icon="add"
            label="Ajouter le patient"
            size="micro"
            onClick={onCreate}
            testId="button-mss-patient-create"
          />
        </>
      )}
    </div>
  )
}

export default MailPatientDetection
